import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icons, LinkButton, Popup } from '@shopline/dashboard-ui';
import { useQueryInitialState } from 'hooks/useInitialState';
import ToastContext from 'contexts/Toast';
import { useQueryMerchant } from '../useMerchant';
import { nanoid } from 'nanoid';
import { Well } from '../components/Well';
import Section from '../components/Section';
import { PopupContent } from '../components/PopupContent';
import usePermissions from 'hooks/usePermissions';
import CircleTickIcon from '../components/CircleTickIcon';
import mobileImg from './mobile.png';
import {
  SectionRow,
  EnhancedWarningAlert,
  FormContainer,
  ImageContainer,
  Image,
  ActionBtns,
  TitleWithLogo,
  LoggedInContainer,
  EmailContainer,
  LogoutContainer,
  LogoutButton,
} from './StyledComponents';
import { useLogoutMerchantGoogleAccount } from '../useLogoutMerchantGoogleAccount';
import { PRODUCT_API_TYPE } from '../constants';

const learnMoreUrl =
  'https://support.shoplineapp.com/hc/zh-tw/articles/23966890906649-Google-Merchant-Center-Content-API';

const McaForm = () => {
  const { t } = useTranslation(['main', 'common']);
  const [isGoogleAuthPopupOpen, setIsGoogleAuthPopupOpen] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const { isSuccess: isMerchantLoaded, data: merchant } = useQueryMerchant();
  const {
    refetch: refetchInitialState,
    isSuccess: isInitialStateLoaded,
    data: initialState,
  } = useQueryInitialState();
  const { createUIToast } = useContext(ToastContext);
  const {
    mutate: logoutMerchantGoogleAccount,
    isLoading: isLoggingOutMerchantGoogleAccount,
  } = useLogoutMerchantGoogleAccount({
    onSuccess: () => {
      refetchInitialState();
    },
    onError: (error) => {
      createUIToast({
        id: nanoid(),
        type: 'alert',
        titleWithParams: {
          key: 'common:Something went wrong. Please try again.',
        },
        duration: 5000,
      });
      console.error(error);
    },
  });
  const canEdit = usePermissions('merchant', 'edit');
  const showLogoutButton =
    isMerchantLoaded &&
    merchant?.productApiType !== PRODUCT_API_TYPE.MERCHANT_API;

  useEffect(() => {
    const handleMessage = (event) => {
      const { code } = event.data;
      if (code === 'login-success') {
        setIsAuthenticating(false);
        refetchInitialState();
        createUIToast({
          id: nanoid(),
          type: 'success',
          titleWithParams: {
            key: "main:You're signed in with Google",
          },
          duration: 5000,
        });
      } else if (code === 'login-error') {
        setIsAuthenticating(false);
        createUIToast({
          id: nanoid(),
          type: 'alert',
          titleWithParams: {
            key: 'common:Something went wrong. Please try again.',
          },
          duration: 5000,
        });
        console.error(event.data);
      }
    };
    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [createUIToast, refetchInitialState]);

  const openGoogleAuth = () => {
    setIsAuthenticating(true);
    setIsGoogleAuthPopupOpen(false);
    window.open('/auth/merchant-google-account', '_blank');
  };

  const openGoogleAuthPopup = () => {
    setIsGoogleAuthPopupOpen(true);
  };

  const merchantGoogleAccount =
    isInitialStateLoaded && initialState?.merchantGoogleAccount;
  const contentApiIntegrated =
    isMerchantLoaded && merchant?.contentApiIntegrated;

  return (
    <Section>
      {!merchantGoogleAccount && (
        <SectionRow>
          <FormContainer>
            <TitleWithLogo>
              <div className="logo">
                <Icons.Brand.ShoplineDefault size="MEDIUM" />
                <Icons.Brand.Google size="MEDIUM" />
              </div>
              SHOPLINE X Google Merchant Center
            </TitleWithLogo>
            <h4>
              {t(
                'Automatically connect Merchant API — no manual setup required',
              )}
            </h4>
            <p>
              {t(
                'Once you sign in, the Merchant API will be automatically connected to SHOPLINE with no manual setup needed. Your product data will be automatically uploaded to Google Merchant Center, allowing them to be accessible in Google Ads, YouTube Live Shopping, and other Google features.',
              )}
            </p>
            <h4>{t('Sign in with your Google account')}</h4>
            <p>
              {t(
                'Once you sign in, We will help you create and connect the Merchant Center account.',
              )}
            </p>
            <EnhancedWarningAlert isShow={contentApiIntegrated}>
              {t(
                'Your store is currently connected to Google Merchant Center (GMC). Signing in will disconnect the existing GMC account and automatically create and connect a new one.',
              )}
            </EnhancedWarningAlert>
            <ActionBtns>
              <Button.Primary
                type="button"
                onClick={openGoogleAuthPopup}
                isLoading={isAuthenticating}
                disabled={!canEdit}
              >
                {t('Sign in with Google')}
              </Button.Primary>
              <LinkButton.Default
                type="button"
                href={learnMoreUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                {t('Learn more')}
              </LinkButton.Default>
            </ActionBtns>
          </FormContainer>
          <ImageContainer>
            <Image src={mobileImg} />
          </ImageContainer>
        </SectionRow>
      )}
      {merchantGoogleAccount && (
        <LoggedInContainer>
          <TitleWithLogo>
            <div className="logo">
              <Icons.Brand.ShoplineDefault size="MEDIUM" />
              <Icons.Brand.Google size="MEDIUM" />
            </div>
            SHOPLINE X Google Merchant Center
          </TitleWithLogo>
          <p>{t('Signed in with Google')}</p>
          <Well>
            <EmailContainer>
              <CircleTickIcon size="MEDIUM" />
              {merchantGoogleAccount.email}
            </EmailContainer>
            {showLogoutButton && (
              <LogoutContainer>
                <LogoutButton
                  onClick={() =>
                    logoutMerchantGoogleAccount(initialState?.merchant?.id)
                  }
                  disabled={!canEdit || isLoggingOutMerchantGoogleAccount}
                >
                  {t('Logout', { ns: 'common' })}
                </LogoutButton>
              </LogoutContainer>
            )}
          </Well>
        </LoggedInContainer>
      )}
      {isGoogleAuthPopupOpen && (
        <Popup
          width="480px"
          title={t('Connect a new Google Merchant Center account?')}
          showCloseBtn={true}
          onClose={() => setIsGoogleAuthPopupOpen(false)}
          renderFooter={() => (
            <>
              <Button.Default
                height="LARGE"
                onClick={() => setIsGoogleAuthPopupOpen(false)}
              >
                {t('Cancel', { ns: 'common' })}
              </Button.Default>
              <Button.Destructive height="LARGE" onClick={openGoogleAuth}>
                {t('Confirm', { ns: 'common' })}
              </Button.Destructive>
            </>
          )}
        >
          <PopupContent>
            <EnhancedWarningAlert isShow={contentApiIntegrated}>
              {t(
                'Your store is currently connected to Google Merchant Center (GMC). Signing in will disconnect the existing GMC account and automatically create and connect a new one.',
              )}
            </EnhancedWarningAlert>
            {t(
              'When your store is connected to a Google Merchant Center account, disconnecting it will have the following effects:',
              { nsSeparator: false },
            )}
            <ul>
              <li>
                {t(
                  'Website ownership will be transferred to the new GMC account.',
                )}
              </li>
              <li>
                {t('Product data will be synced to the new GMC account.')}
              </li>
              <li>
                {t(
                  'Products in the original GMC account will be marked as "Disapproved".',
                )}
              </li>
              <li>
                {t(
                  'Ad campaigns linked to the original GMC account will be interrupted.',
                )}
              </li>
              <li>
                {t(
                  'The original GMC account will be deactivated and can no longer be used.',
                )}
              </li>
            </ul>
          </PopupContent>
        </Popup>
      )}
    </Section>
  );
};

export default McaForm;
